const feature = {
  slug: "instagram-comment-to-dm-automation",
  path: "/features/instagram-comment-to-dm-automation",
  eyebrow: "Instagram comment-to-DM automation",
  h1: "Turn Instagram comments into private conversations that end in orders",
  seoTitle: "Instagram Comment-to-DM Automation | Checkout DM",
  description:
    "Checkout DM sends a private message when someone comments your trigger word on a post or Reel, then carries the buyer through size, cart and payment.",
  intro:
    "You post a Reel, the comments start, and you spend the evening typing “DM me” under each one. Comment-to-DM does that for you. A comment with your trigger word starts a private conversation about the exact product, and the buyer can order without leaving Instagram.",
  glance: [
    { label: "Available from", value: "The Creator plan upward" },
    { label: "Messages per person", value: "Once per post" },
    { label: "Works on", value: "Posts and Reels you choose" },
  ],
  blocks: [
    { type: "h2", text: "What happens when someone comments" },
    {
      type: "p",
      text:
        "Comment-to-DM watches the comments on the posts or Reels you choose. When someone comments your trigger word, it sends them a private message with the product, the price or the start of your checkout. The buyer stays inside Instagram, and you do not type a thing.",
    },
    {
      type: "chat",
      shop: "yourshop",
      title: "After a comment",
      caption: "Illustrative example. Your own message uses your products and wording.",
      messages: [
        { from: "time", text: "Replied to a comment on your Reel" },
        { from: "shop", text: "Hi! Thanks for commenting. Here is the Linen Kurta Set, ₹1,899. Which size would you like?" },
        { from: "buyer", text: "M" },
        { from: "shop", text: "Added to your cart. Where should we deliver it?" },
      ],
    },
    {
      type: "p",
      text:
        "Each person is messaged once per post, so a customer who comments twice does not get duplicate messages.",
    },

    { type: "h2", text: "Why it beats “DM me”" },
    {
      type: "ul",
      items: [
        "**No extra step for the buyer.** They comment, and the conversation comes to them.",
        "**The reply arrives in seconds**, while interest is fresh, instead of after your evening of replying.",
        "**Every comment becomes a conversation you can track.** You see who asked, what they chose and where they stopped.",
      ],
    },

    { type: "h2", text: "Set it up in five steps" },
    {
      type: "ol",
      items: [
        "**Choose the post or Reel.** Pick one that already shows a specific product, so the message can talk about exactly that item.",
        "**Pick one trigger word.** Short, easy to type and unlikely to appear by accident, such as a product name or the word LINK.",
        "**Connect it to one product.** A message about the exact item they saw works better than a general hello.",
        "**Tell people what to comment.** Put it in the caption, on screen in the Reel and in what you say out loud.",
        "**Test from a second Instagram account** before you post publicly.",
      ],
    },
    {
      type: "p",
      text:
        "Our guides on [comment-to-DM for Instagram orders](/blog/comment-to-dm-instagram-orders) and [turning Reels into sales](/blog/turn-instagram-reels-into-sales) go deeper on wording and mistakes to avoid.",
    },

    { type: "h2", text: "Rules it follows" },
    {
      type: "p",
      text:
        "Meta allows one private reply per comment, within 7 days of the comment. The first message lands in the person’s inbox if they follow you, or in message requests if they do not, and the conversation continues once they reply. That is why the first message should be short and easy to answer. Checkout DM connects through Instagram’s official login, so your password is never shared. Read [whether Instagram DM automation is safe](/blog/is-instagram-dm-automation-safe-2026) for the full list of limits.",
    },

    { type: "h2", text: "What it does not do" },
    {
      type: "ul",
      items: [
        "**It does not message people who do not comment.** A comment with your trigger word is what starts it.",
        "**It does not fix a vague trigger word.** Common words like “yes” or “price” fire for all kinds of reasons, so choose a specific one.",
        "**It does not replace a clear call to action.** If people do not know which word to comment, nothing starts.",
      ],
    },

    { type: "h2", text: "Who it suits" },
    {
      type: "p",
      text:
        "Sellers who post Reels or product posts and get comments asking for price or availability, creators who give away free digital files in exchange for a comment, and anyone who is tired of copying prices into replies. On the Creator plan, which is digital-only, you can also hand out free digital products from a comment and use a Preview or Buy button for affiliate links.",
    },
  ],
  related: [
    { to: "/features/instagram-dm-automation", title: "Instagram DM automation", description: "The full chat flow after the first message." },
    { to: "/features/instagram-story-reply-automation", title: "Story reply automation", description: "Product cards from a Story reply." },
    { to: "/features/instagram-product-catalog-checkout", title: "Catalog and checkout", description: "Sizes, stock, payment and delivery details in the chat." },
    { to: "/pricing", title: "Checkout DM pricing", description: "Comment-to-DM starts on the Creator plan." },
  ],
  faqs: [
    {
      q: "What is Instagram comment-to-DM automation?",
      a: "It automatically sends a private message to anyone who comments your trigger word on a post or Reel you chose. With Checkout DM, that message starts a checkout conversation about the product.",
    },
    {
      q: "Which Checkout DM plan includes comment-to-DM?",
      a: "Comment-to-DM starts on the Creator plan (₹499 or $9 a month) and is included on every plan above it. It is not part of the Free plan.",
    },
    {
      q: "Will a customer get the same message twice?",
      a: "No. Each person is messaged once per post, even if they comment more than once.",
    },
    {
      q: "Does the buyer need to follow me?",
      a: "No. If they follow you, the first message lands in their inbox. If they do not, it lands in message requests, and the conversation continues once they reply.",
    },
    {
      q: "Is comment-to-DM allowed by Instagram?",
      a: "Yes, when it uses Instagram’s official connection and stays within Meta’s limits, including one private reply per comment within 7 days. Checkout DM connects through Meta’s official login.",
    },
  ],
};

export default feature;
